import { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

// Grouped navigation. Items can be anchor hashes (#hero) or routes (/blog).
const NAV_GROUPS = [
  {
    label: "What we do",
    items: [
      { label: "Services", href: "#services" },
      { label: "How we run it", href: "#how-we-run-it" },
      { label: "Channels we cover", href: "#channels" },
    ],
  },
  {
    label: "Why us",
    items: [
      { label: "Why Thoratech", href: "#why-us" },
      { label: "Industries we serve", href: "#industries" },
      { label: "Success story", href: "#success-story" },
    ],
  },
  { label: "Blog", to: "/blog" },
  {
    label: "About",
    items: [
      { label: "About & Founder", href: "#about" },
      { label: "Privacy policy", to: "/privacy" },
    ],
  },
  { label: "Careers", to: "/careers" },
];

// Preferred left-to-right ordering: What we do | Why us | About | Careers | Blog | Contact | CTA
const NAV_ORDER = ["What we do", "Why us", "About", "Careers", "Blog"];
NAV_GROUPS.sort((a, b) => NAV_ORDER.indexOf(a.label) - NAV_ORDER.indexOf(b.label));

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openGroup, setOpenGroup] = useState(null); // desktop hover state
  const [mobileGroup, setMobileGroup] = useState(null);
  const closeTimer = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToAnchor = (e, href) => {
    e.preventDefault();
    setOpen(false);
    setOpenGroup(null);
    if (onHome) {
      const el = document.querySelector(href);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 110;
        window.scrollTo({ top, behavior: "smooth" });
      }
    } else {
      navigate("/" + href);
    }
  };

  const goLogo = (e) => {
    e.preventDefault();
    setOpen(false);
    if (onHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const handleGroupEnter = (label) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenGroup(label);
  };
  const handleGroupLeave = () => {
    closeTimer.current = setTimeout(() => setOpenGroup(null), 150);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-[#01080E]/10"
          : "bg-white/85 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-[80px] md:h-[96px] flex items-center justify-between gap-4">
        <a
          href="/"
          onClick={goLogo}
          data-testid="nav-logo"
          className="flex items-center flex-shrink-0"
          aria-label="Thoratech home"
        >
          <img
            src="/brand/TT_Wordmark_Light.svg"
            alt="Thoratech"
            className="h-[52px] md:h-[68px] w-auto max-w-[58vw]"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_GROUPS.map((g) => {
            if (g.to) {
              return (
                <Link
                  key={g.label}
                  to={g.to}
                  data-testid={`nav-link-${g.label.toLowerCase()}`}
                  className="px-4 py-2 text-sm font-medium text-[#01080E]/85 hover:text-[#2AC0A5] transition-colors duration-200"
                >
                  {g.label}
                </Link>
              );
            }
            const isOpen = openGroup === g.label;
            return (
              <div
                key={g.label}
                className="relative"
                onMouseEnter={() => handleGroupEnter(g.label)}
                onMouseLeave={handleGroupLeave}
              >
                <button
                  type="button"
                  data-testid={`nav-group-${g.label.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5 transition-colors duration-200 ${
                    isOpen ? "text-[#2AC0A5]" : "text-[#01080E]/85 hover:text-[#2AC0A5]"
                  }`}
                >
                  {g.label}
                  <ChevronDown
                    size={14}
                    className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
                <div
                  data-testid={`nav-dropdown-${g.label.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`absolute top-full left-0 min-w-[240px] bg-white border border-[#01080E]/10 shadow-xl transition-all duration-150 ${
                    isOpen
                      ? "opacity-100 visible translate-y-0"
                      : "opacity-0 invisible -translate-y-1 pointer-events-none"
                  }`}
                >
                  <div className="py-2">
                    {g.items.map((it) =>
                      it.to ? (
                        <Link
                          key={it.label}
                          to={it.to}
                          onClick={() => setOpenGroup(null)}
                          data-testid={`nav-item-${it.label.replace(/\s+/g, "-").toLowerCase()}`}
                          className="block px-5 py-3 text-sm text-[#01080E]/85 hover:bg-[#FAFAF8] hover:text-[#2AC0A5] transition-colors"
                        >
                          {it.label}
                        </Link>
                      ) : (
                        <a
                          key={it.label}
                          href={it.href}
                          onClick={(e) => goToAnchor(e, it.href)}
                          data-testid={`nav-item-${it.label.replace(/\s+/g, "-").toLowerCase()}`}
                          className="block px-5 py-3 text-sm text-[#01080E]/85 hover:bg-[#FAFAF8] hover:text-[#2AC0A5] transition-colors"
                        >
                          {it.label}
                        </a>
                      )
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <a
            href="#contact"
            onClick={(e) => goToAnchor(e, "#contact")}
            data-testid="nav-link-contact-us"
            className="px-4 py-2 text-sm font-medium text-[#01080E]/85 hover:text-[#2AC0A5] transition-colors duration-200"
          >
            Contact Us
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            onClick={(e) => goToAnchor(e, "#contact")}
            data-testid="nav-cta-button"
            className="hidden sm:inline-flex items-center bg-[#01080E] text-white text-sm font-semibold px-5 md:px-6 py-2.5 md:py-3 hover:bg-[#2AC0A5] hover:text-[#01080E] transition-colors duration-200 whitespace-nowrap"
          >
            Book a free audit
          </a>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 text-[#01080E]"
            onClick={() => setOpen((s) => !s)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-[#01080E]/10 bg-white max-h-[calc(100vh-80px)] overflow-y-auto">
          <div className="max-w-7xl mx-auto px-5 py-3 flex flex-col">
            {NAV_GROUPS.map((g) => {
              if (g.to) {
                return (
                  <Link
                    key={g.label}
                    to={g.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${g.label.toLowerCase()}`}
                    className="py-4 text-base font-medium text-[#01080E] border-b border-[#01080E]/5"
                  >
                    {g.label}
                  </Link>
                );
              }
              const isOpen = mobileGroup === g.label;
              return (
                <div key={g.label} className="border-b border-[#01080E]/5">
                  <button
                    type="button"
                    onClick={() => setMobileGroup(isOpen ? null : g.label)}
                    data-testid={`mobile-group-${g.label.replace(/\s+/g, "-").toLowerCase()}`}
                    className="w-full py-4 flex items-center justify-between text-base font-medium text-[#01080E]"
                  >
                    {g.label}
                    <ChevronDown
                      size={18}
                      className={`text-[#01080E]/55 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {isOpen && (
                    <div className="pl-4 pb-3 flex flex-col">
                      {g.items.map((it) =>
                        it.to ? (
                          <Link
                            key={it.label}
                            to={it.to}
                            onClick={() => setOpen(false)}
                            data-testid={`mobile-item-${it.label.replace(/\s+/g, "-").toLowerCase()}`}
                            className="py-2.5 text-sm text-[#01080E]/85 hover:text-[#2AC0A5] transition-colors"
                          >
                            {it.label}
                          </Link>
                        ) : (
                          <a
                            key={it.label}
                            href={it.href}
                            onClick={(e) => goToAnchor(e, it.href)}
                            data-testid={`mobile-item-${it.label.replace(/\s+/g, "-").toLowerCase()}`}
                            className="py-2.5 text-sm text-[#01080E]/85 hover:text-[#2AC0A5] transition-colors"
                          >
                            {it.label}
                          </a>
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <a
              href="#contact"
              onClick={(e) => goToAnchor(e, "#contact")}
              data-testid="mobile-nav-link-contact-us"
              className="py-4 text-base font-medium text-[#01080E] border-b border-[#01080E]/5"
            >
              Contact Us
            </a>
            <a
              href="#contact"
              onClick={(e) => goToAnchor(e, "#contact")}
              data-testid="mobile-nav-cta"
              className="mt-4 inline-flex items-center justify-center bg-[#01080E] text-white text-sm font-semibold px-5 py-3.5"
            >
              Book a free audit
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
